import { AssetsClientError, toAssetsClientError } from './errors';
import { assertAssetFilePolicy } from './policy';
import type {
  AssetRecord,
  AssetsClientOptions,
  AssetsTransport,
  CompleteAssetUploadInput,
  DeleteAssetInput,
  GetAssetByIdInput,
  ListAssetsInput,
  ListAssetsResult,
  PrepareAssetUploadInput,
  PrepareAssetUploadResult,
  UploadAssetInput,
  UploadAssetResult,
  UploadProgress,
  UpdateAssetInput,
} from './types';

function normalizeMimeType(mimeType: string): string {
  return mimeType.split(';')[0]?.trim().toLowerCase();
}

async function uploadObject(args: {
  file: PrepareAssetUploadInput['file'];
  upload: PrepareAssetUploadResult['upload'];
  signal?: AbortSignal;
  onProgress?: (progress: UploadProgress) => void;
}): Promise<void> {
  const totalBytes = args.file.size;
  let loadedBytes = 0;

  const notifyProgress = () => {
    if (!args.onProgress) return;
    const percent = totalBytes > 0 ? Math.round((loadedBytes / totalBytes) * 100) : 0;
    args.onProgress({ loadedBytes, totalBytes, percent: Math.min(percent, 100) });
  };

  notifyProgress();

  const arrayBuffer = await args.file.arrayBuffer();
  loadedBytes = totalBytes;
  notifyProgress();

  let response: Response;
  try {
    response = await fetch(args.upload.url, {
      method: args.upload.method,
      headers: args.upload.headers,
      body: arrayBuffer,
      signal: args.signal,
    });
  } catch (error) {
    throw new AssetsClientError({
      code: 'UPLOAD_FAILED',
      message:
        'Failed to reach object storage upload URL. This is usually a CORS/preflight or network issue.',
      details: {
        uploadUrl: args.upload.url,
      },
      cause: error,
    });
  }

  if (!response.ok) {
    const errorText = await response.text().catch(() => null);
    throw new AssetsClientError({
      code: 'UPLOAD_FAILED',
      message: `Signed upload failed (${response.status})`,
      details: {
        status: response.status,
        body: errorText,
      },
    });
  }

  loadedBytes = totalBytes;
  notifyProgress();
}

export async function prepareAssetUpload(
  input: PrepareAssetUploadInput,
  options: { transport: AssetsTransport; validatePolicy?: boolean },
): Promise<PrepareAssetUploadResult> {
  try {
    if (options.validatePolicy !== false) {
      assertAssetFilePolicy({
        file: input.file,
        assetType: input.assetType,
      });
    }

    return await options.transport.prepareUpload({
      filename: input.file.name,
      mimeType: normalizeMimeType(input.file.type),
      sizeBytes: input.file.size,
      assetType: input.assetType,
      ownerType: input.ownerType,
      ownerId: input.ownerId,
      visibility: input.visibility,
      tags: input.tags,
      source: input.source,
      uploadedByType: input.uploadedByType,
      uploadedById: input.uploadedById,
      assetMetadata: input.assetMetadata,
    });
  } catch (error) {
    throw toAssetsClientError({
      code: 'PREPARE_FAILED',
      message: 'Failed to prepare asset upload',
      cause: error,
      details: {
        filename: input.file.name,
        mimeType: input.file.type,
        sizeBytes: input.file.size,
        assetType: input.assetType,
      },
    });
  }
}

export async function completeAssetUpload(
  input: CompleteAssetUploadInput,
  options: { transport: AssetsTransport },
): Promise<AssetRecord> {
  try {
    return await options.transport.completeUpload(input);
  } catch (error) {
    throw toAssetsClientError({
      code: 'COMPLETE_FAILED',
      message: 'Failed to complete asset upload',
      cause: error,
      details: { assetId: input.assetId },
    });
  }
}

export async function uploadAsset(
  input: UploadAssetInput,
  options: { transport: AssetsTransport; validatePolicy?: boolean },
): Promise<UploadAssetResult> {
  const prepared = await prepareAssetUpload(input.prepare, options);

  try {
    await uploadObject({
      file: input.prepare.file,
      upload: prepared.upload,
      signal: input.signal,
      onProgress: input.onProgress,
    });
  } catch (error) {
    throw toAssetsClientError({
      code: 'UPLOAD_FAILED',
      message: 'Failed to upload asset bytes',
      cause: error,
      details: {
        assetId: prepared.asset.id,
        objectKey: prepared.asset.objectKey,
      },
    });
  }

  const completed = await completeAssetUpload({ assetId: prepared.asset.id }, options);

  return {
    preparedAsset: prepared.asset,
    asset: completed,
  };
}

export async function listAssets(
  input: ListAssetsInput,
  options: { transport: AssetsTransport },
): Promise<ListAssetsResult> {
  try {
    return await options.transport.list(input);
  } catch (error) {
    throw toAssetsClientError({
      code: 'LIST_FAILED',
      message: 'Failed to list assets',
      cause: error,
      details: input,
    });
  }
}

export async function getAssetById(
  input: GetAssetByIdInput,
  options: { transport: AssetsTransport },
): Promise<AssetRecord> {
  try {
    return await options.transport.getById(input);
  } catch (error) {
    throw toAssetsClientError({
      code: 'GET_FAILED',
      message: 'Failed to fetch asset',
      cause: error,
      details: input,
    });
  }
}

export async function updateAsset(
  input: UpdateAssetInput,
  options: { transport: AssetsTransport },
): Promise<AssetRecord> {
  try {
    return await options.transport.update(input);
  } catch (error) {
    throw toAssetsClientError({
      code: 'UPDATE_FAILED',
      message: 'Failed to update asset',
      cause: error,
      details: input,
    });
  }
}

export async function deleteAsset(
  input: DeleteAssetInput,
  options: { transport: AssetsTransport },
): Promise<AssetRecord> {
  try {
    return await options.transport.delete(input);
  } catch (error) {
    throw toAssetsClientError({
      code: 'DELETE_FAILED',
      message: 'Failed to delete asset',
      cause: error,
      details: input,
    });
  }
}

export function createAssetsClient(options: AssetsClientOptions) {
  return {
    prepareAssetUpload: (input: PrepareAssetUploadInput) =>
      prepareAssetUpload(input, {
        transport: options.transport,
        validatePolicy: options.validatePolicy,
      }),
    uploadAsset: (input: UploadAssetInput) =>
      uploadAsset(input, {
        transport: options.transport,
        validatePolicy: options.validatePolicy,
      }),
    completeAssetUpload: (input: CompleteAssetUploadInput) =>
      completeAssetUpload(input, { transport: options.transport }),
    listAssets: (input: ListAssetsInput = {}) =>
      listAssets(input, { transport: options.transport }),
    getAssetById: (input: GetAssetByIdInput) =>
      getAssetById(input, { transport: options.transport }),
    updateAsset: (input: UpdateAssetInput) => updateAsset(input, { transport: options.transport }),
    deleteAsset: (input: DeleteAssetInput) => deleteAsset(input, { transport: options.transport }),
  };
}

export type AssetsClient = ReturnType<typeof createAssetsClient>;
